"use client"

import { useQuery } from "@tanstack/react-query"
import { createContext, type ReactNode, useContext } from "react"
import { useORPC } from "@/providers/orpc-provider"

type GlobalConfigType = {
	PUBLIC_ALLOW_GUEST_USERS: boolean
	PUBLIC_MAINTENANCE_MODE_ENABLED: boolean
	PUBLIC_TURNSTILE_ENABLED: boolean
}

interface GlobalConfigContextProps {
	globalConfig: GlobalConfigType | null
	isLoading?: boolean
	error?: Error | null
	refetch?: () => void
}

const GlobalConfigContext = createContext<GlobalConfigContextProps | undefined>(
	undefined,
)

export const GlobalConfigProvider = ({
	children,
	initialConfig,
}: {
	children: ReactNode
	initialConfig: GlobalConfigType | null
}) => {
	const orpc = useORPC()

	const {
		data: clientConfig,
		isLoading,
		error,
		refetch,
	} = useQuery({
		queryKey: ["globalConfig"],
		queryFn: async () => {
			try {
				const response =
					await orpc.cms.globalConfig.public_getAllPublicConfigs.call()

				// Backend now returns strings for all values; normalize to booleans
				const toBoolean = (value: unknown, fallback: boolean): boolean => {
					if (typeof value === "boolean") return value
					if (typeof value === "string") {
						const v = value.trim().toLowerCase()
						if (["true", "1", "yes", "y"].includes(v)) return true
						if (["false", "0", "no", "n"].includes(v)) return false
					}
					return fallback
				}

				const normalized: GlobalConfigType = {
					PUBLIC_ALLOW_GUEST_USERS: toBoolean(
						(response as Record<string, unknown>)["PUBLIC_ALLOW_GUEST_USERS"],
						initialConfig?.PUBLIC_ALLOW_GUEST_USERS ?? true,
					),
					PUBLIC_MAINTENANCE_MODE_ENABLED: toBoolean(
						(response as Record<string, unknown>)[
							"PUBLIC_MAINTENANCE_MODE_ENABLED"
						],
						initialConfig?.PUBLIC_MAINTENANCE_MODE_ENABLED ?? false,
					),
					PUBLIC_TURNSTILE_ENABLED: toBoolean(
						(response as Record<string, unknown>)["PUBLIC_TURNSTILE_ENABLED"],
						initialConfig?.PUBLIC_TURNSTILE_ENABLED ?? false,
					),
				}

				return normalized
			} catch (_err) {
				return initialConfig
			}
		},
		initialData: initialConfig,
		staleTime: 15 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
		retry: 2,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
	})

	const value = {
		globalConfig: clientConfig,
		isLoading,
		error: error as Error | null,
		refetch,
	}

	return (
		<GlobalConfigContext.Provider value={value}>
			{children}
		</GlobalConfigContext.Provider>
	)
}

export const useGlobalConfig = (): GlobalConfigContextProps => {
	const context = useContext(GlobalConfigContext)
	if (context === undefined) {
		throw new Error(
			"useGlobalConfig must be used within a GlobalConfigProvider",
		)
	}
	return context
}
